import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import "./supplierProfile.css";
import { useSupplierContext } from "../../../context/SupplierContext";
import { usePaymentVoucherContext } from "../../../context/PaymentVoucherContext";
import {
  FaUser,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaMoneyBillWave,
  FaCalendar,
  FaFileInvoice,
} from "react-icons/fa";

const SupplierProfile = () => {
  const { id } = useParams();
  const { getSupplierProfile } = useSupplierContext();
  const { paymentVouchers: allPaymentVouchers, fetchPaymentVouchers } =
    usePaymentVoucherContext();

  const [supplier, setSupplier] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [salesInvoices, setSalesInvoices] = useState([]);
  const [paymentVouchers, setPaymentVouchers] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSupplierData();
  }, [id]);

  const fetchSupplierData = async () => {
    try {
      setLoading(true);
      const data = await getSupplierProfile(id);
      setSupplier(data.supplier);
      setInvoices(data.invoices || []);
      setSalesInvoices(data.sales_invoices || []);
      setStatistics(data.statistics);
      setError(null);

      // جلب سندات الصرف
      await fetchPaymentVouchers();
    } catch (err) {
      setError("فشل في جلب بيانات المورد");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // تصفية سندات الصرف الخاصة بهذا المورد
  useEffect(() => {
    if (allPaymentVouchers && allPaymentVouchers.length > 0) {
      const supplierVouchers = allPaymentVouchers.filter(
        (v) => v.supplier_id === parseInt(id)
      );
      setPaymentVouchers(supplierVouchers);
    }
  }, [allPaymentVouchers, id]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("ar-en", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="supplier-profile-container">
        <div className="loading">جاري التحميل...</div>
      </div>
    );
  }

  if (error || !supplier) {
    return (
      <div className="supplier-profile-container">
        <div className="error-box">{error || "المورد غير موجود"}</div>
      </div>
    );
  }

  return (
    <div className="supplier-profile-container">
      <div className="wrapper">
        {/* Header */}
        <div className="profile-header">
          <div className="header-content">
            <div className="supplier-avatar">
              <FaUser />
            </div>
            <div className="header-info">
              <h1>{supplier.full_name}</h1>
              <p className="join-date">
                <FaCalendar /> تمت الاضافة في {formatDate(supplier.created_at)}
              </p>
            </div>
          </div>
          <Link to={`/update-supplier/${id}`} className="edit-btn">
            تعديل البيانات
          </Link>
        </div>
        {/* Statistics Cards */}}
        <div className="statistics-grid">
          <div className="stat-card balance-card">
            <div className="stat-icon">
              <FaMoneyBillWave />
            </div>
            <div className="stat-info">
              <p className="stat-label">الرصيد الحالي</p>
              <h3 className="stat-value">
                {parseFloat(supplier.balance).toFixed(2)} ₪
              </h3>
            </div>
          </div>

          <div className="stat-card invoices-card">
            <div className="stat-icon">
              <FaFileInvoice />
            </div>
            <div className="stat-info">
              <p className="stat-label">عدد فواتير الشراء والبيع</p>
              <h3 className="stat-value">
                {(statistics?.total_invoices || 0) +
                  (statistics?.total_sales_invoices || 0)}
              </h3>
            </div>
          </div>

          <div className="stat-card total-card">
            <div className="stat-icon">
              <FaMoneyBillWave />
            </div>
            <div className="stat-info">
              <p className="stat-label">إجمالي المشتريات</p>
              <h3 className="stat-value">
                {parseFloat(statistics?.total_amount || 0).toFixed(2)} ₪
              </h3>
            </div>
          </div>

          <div className="stat-card total-card">
            <div className="stat-icon">
              <FaMoneyBillWave />
            </div>
            <div className="stat-info">
              <p className="stat-label">إجمالي المبيعات للمورد</p>
              <h3 className="stat-value">
                {parseFloat(statistics?.total_sales_amount || 0).toFixed(2)} ₪
              </h3>
            </div>
          </div>
        </div>
        {/* Contact Information */}
        <div className="info-section">
          <h2>معلومات الاتصال</h2>
          <div className="info-grid">
            <div className="info-item">
              <FaPhone className="info-icon" />
              <div>
                <p className="info-label">رقم الهاتف</p>
                <p className="info-value">{supplier.phone || "غير متوفر"}</p>
              </div>
            </div>

            <div className="info-item">
              <FaUser className="info-icon" />
              <div>
                <p className="info-label">الوصف</p>
                <p className="info-value">
                  {supplier.description || "غير متوفر"}
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* Purchase Invoices Table */}
        <div className="invoices-section">
          <h2>فواتير الشراء</h2>
          {invoices.length === 0 ? (
            <div className="no-data">لا توجد فواتير شراء لهذا المورد</div>
          ) : (
            <div className="table-container">
              <table className="invoices-table">
                <thead>
                  <tr>
                    <th>رقم الفاتورة</th>
                    <th>التاريخ</th>
                    <th>عدد الأصناف</th>
                    <th>المبلغ الإجمالي</th>
                    <th>الحالة</th>
                    <th>ملاحظات</th>
                    <th>إجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => (
                    <tr key={invoice.id}>
                      <td>#{invoice.id}</td>
                      <td>{formatDate(invoice.created_at)}</td>
                      <td>{invoice.items_count}</td>
                      <td className="amount">
                        {parseFloat(invoice.total_amount).toFixed(2)} ₪
                      </td>
                      <td>
                        {invoice.is_canceled == 1 ? (
                          <span
                            style={{
                              padding: "4px 12px",
                              backgroundColor: "#fee2e2",
                              color: "#dc2626",
                              borderRadius: "4px",
                              fontWeight: "600",
                              fontSize: "13px",
                            }}
                          >
                            ملغية
                          </span>
                        ) : (
                          <span
                            style={{
                              padding: "4px 12px",
                              backgroundColor: "#d1fae5",
                              color: "#059669",
                              borderRadius: "4px",
                              fontWeight: "600",
                              fontSize: "13px",
                            }}
                          >
                            نشطة
                          </span>
                        )}
                      </td>
                      <td className="notes">{invoice.notes || "-"}</td>
                      <td>
                        <Link
                          to={`/purchase-invoice/${invoice.id}`}
                          className="view-btn"
                        >
                          عرض
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {/* Sales Invoices Table */}
        <div className="invoices-section">
          <h2>فواتير البيع للمورد</h2>
          {salesInvoices.length === 0 ? (
            <div className="no-data">لا توجد فواتير بيع لهذا المورد</div>
          ) : (
            <div className="table-container">
              <table className="invoices-table">
                <thead>
                  <tr>
                    <th>رقم الفاتورة</th>
                    <th>التاريخ</th>
                    <th>عدد الأصناف</th>
                    <th>المبلغ الإجمالي</th>
                    <th>الحالة</th>
                    <th>ملاحظات</th>
                    <th>إجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {salesInvoices.map((invoice) => (
                    <tr key={invoice.id}>
                      <td>#{invoice.id}</td>
                      <td>{formatDate(invoice.date || invoice.created_at)}</td>
                      <td>{invoice.items_count}</td>
                      <td className="amount">
                        {parseFloat(invoice.total_amount).toFixed(2)} ₪
                      </td>
                      <td>{invoice.is_canceled == 1 ? "ملغية" : "نشطة"}</td>
                      <td className="notes">{invoice.notes || "-"}</td>
                      <td>
                        <Link to={`/sales-invoice/${invoice.id}`} className="view-btn">
                          عرض
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {/* Payment Vouchers Table */}}
        <div className="invoices-section">
          <h2>سندات الصرف</h2>
          {paymentVouchers.length === 0 ? (
            <div className="no-data">لا توجد سندات صرف لهذا المورد</div>
          ) : (
            <div className="table-container">
              <table className="invoices-table">
                <thead>
                  <tr>
                    <th>رقم السند</th>
                    <th>رقم الفاتورة</th>
                    <th>التاريخ</th>
                    <th>المبلغ</th>
                    <th>نوع الدفع</th>
                    <th>معلومات الشيك</th>
                    <th>ملاحظات</th>
                    <th>إجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentVouchers.map((voucher) => (
                    <tr key={voucher.id}>
                      <td>#{voucher.id}</td>
                      <td>
                        <Link
                          to={`/purchase-invoice/${voucher.invoice_id}`}
                          style={{
                            color: "#0066ff",
                            fontWeight: 600,
                            textDecoration: "none",
                          }}
                        >
                          #{voucher.invoice_id}
                        </Link>
                      </td>
                      <td>{formatDate(voucher.created_at)}</td>
                      <td className="amount">
                        {parseFloat(voucher.amount).toFixed(2)} ₪
                      </td>
                      <td>
                        <span
                          style={{
                            padding: "4px 12px",
                            borderRadius: "12px",
                            fontSize: "0.875rem",
                            fontWeight: 600,
                            backgroundColor:
                              voucher.payment_type === "كاش"
                                ? "#d1fae5"
                                : "#dbeafe",
                            color:
                              voucher.payment_type === "كاش"
                                ? "#065f46"
                                : "#1e40af",
                          }}
                        >
                          {voucher.payment_type}
                        </span>
                      </td>
                      <td>
                        {voucher.payment_type === "شيك" ? (
                          <div
                            style={{ fontSize: "0.85rem", lineHeight: "1.6" }}
                          >
                            <div>
                              <strong>رقم:</strong> {voucher.check_number}
                            </div>
                            <div>
                              <strong>البنك:</strong> {voucher.check_bank}
                            </div>
                            <div>
                              <strong>الاستحقاق:</strong>{" "}
                              {formatDate(voucher.check_due_date)}
                            </div>
                          </div>
                        ) : (
                          <span style={{ color: "#999", fontSize: "18px" }}>
                            -
                          </span>
                        )}
                      </td>
                      <td className="notes">{voucher.notes || "-"}</td>
                      <td>
                        <Link
                          to={`/payment-voucher/${voucher.id}`}
                          className="view-btn"
                        >
                          عرض
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupplierProfile;
